import React, { useState, useEffect } from "react";
import { app } from "../../firebase";
import { getFirestore, collection, query, getDocs, setDoc, doc, Timestamp } from "firebase/firestore";
import { Button, Modal } from "react-bootstrap";
import "./Viewmessages.css";

const db = getFirestore(app);

const Viewmessages = ({ theme }) => {
    const [messages, setMessages] = useState([]);
    const [showReplyModal, setShowReplyModal] = useState(false);
    const [selectedEmail, setSelectedEmail] = useState("");
    const [replyMessage, setReplyMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");

    useEffect(() => {
        const fetchMessages = async () => {
            try {
                const userMessagesQuery = query(collection(db, "contacts"));
                const userMessagesSnapshot = await getDocs(userMessagesQuery);
                const userData = userMessagesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setMessages(userData);
            } catch (error) {
                console.error("Error fetching messages:", error);
            }
        };
        fetchMessages();
    }, []);

    const handleReply = (email) => {
        setSelectedEmail(email);
        setShowReplyModal(true);
    };

    const handleCloseReplyModal = () => {
        setShowReplyModal(false);
        setReplyMessage("");
    };

    const handleSendReply = async () => {
        setLoading(true);
        try {
            const unrepliedMessages = messages.filter(message => message.email === selectedEmail && !message.replied);
            if (unrepliedMessages.length > 0) {
                const batch = [];
                const replyTimestamp = Timestamp.now();
                unrepliedMessages.forEach(message => {
                    const messageDocRef = doc(db, "contacts", message.id);
                    batch.push(setDoc(messageDocRef, {
                        replied: true,
                        replyMessage: replyMessage,
                        repliedTimestamp: replyTimestamp,
                    }, { merge: true }));
                });
                await Promise.all(batch);
                setMessages(messages.map(message => {
                    if (message.email === selectedEmail && !message.replied) {
                        return { ...message, replied: true, replyMessage, repliedTimestamp: replyTimestamp };
                    }
                    return message;
                }));
                setSuccessMessage("Reply sent successfully!");
            } else {
                console.log("No unreplied messages for this email.");
            }
            setTimeout(() => {
                setSuccessMessage("");
            }, 3000);
        } catch (error) {
            console.error("Error sending reply:", error);
        }
        setLoading(false);
    };

    const renderUnrepliedMessages = () => {
        const groupedByUser = {};
        messages.forEach(message => {
            if (!message.replied) {
                if (!groupedByUser[message.email]) {
                    groupedByUser[message.email] = [];
                }
                groupedByUser[message.email].push(message);
            }
        });

        const sortedUnrepliedUsers = Object.entries(groupedByUser).sort((a, b) => {
            const latestMessageA = Math.max(...a[1].map(msg => msg.timestamp ? msg.timestamp.seconds : 0));
            const latestMessageB = Math.max(...b[1].map(msg => msg.timestamp ? msg.timestamp.seconds : 0));
            return latestMessageB - latestMessageA;
        });

        return (
            <div className={`container ${theme}`}>
                <h2 style={{ color: theme === "dark" ? "white" : "black", fontWeight: "bold" }}>Unreplied Messages</h2>
                {sortedUnrepliedUsers.length === 0 && <p style={{ color: theme === "dark" ? "white" : "black" }}>No unreplied messages</p>}

                {sortedUnrepliedUsers.map(([email, messages]) => {
                    const sortedMessages = messages.sort((a, b) => (b.timestamp ? b.timestamp.seconds : 0) - (a.timestamp ? a.timestamp.seconds : 0));
                    return (
                        <div key={email}>
                            <p style={{ margin: "auto", paddingBottom: "10px", fontSize: "20px", color: theme === "dark" ? "white" : "black" }}>{email}</p>
                            {sortedMessages.map((message) => (
                                <div key={message.id} className="animated-container">
                                    <div className="message-content">
                                        <div></div>
                                        <strong>Message:</strong> {message.message}
                                    </div>
                                </div>
                            ))}
                            {!messages[0]?.replied && (
                                <div style={{ textAlign: "center" }}>
                                    <Button style={{ backgroundColor: "whitesmoke", color: "black" }} onClick={() => handleReply(email)}>Reply</Button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderRepliedMessages = () => {
        const groupedByAdminReply = groupMessagesByAdminReply(messages.filter(message => message.replied));
        const sortedRepliedUsers = groupedByAdminReply.sort((a, b) => {
            const latestReplyA = Math.max(...a.messages.map(msg => msg.repliedTimestamp ? msg.repliedTimestamp.seconds : -Infinity));
            const latestReplyB = Math.max(...b.messages.map(msg => msg.repliedTimestamp ? msg.repliedTimestamp.seconds : -Infinity));
            return latestReplyB - latestReplyA;
        });

        return (
            <div>
                <h2 style={{ color: theme === "dark" ? "white" : "black", fontWeight: "bold" }}>Replied Messages</h2>
                {sortedRepliedUsers.map((group, index) => (
                    <div key={index}>
                        <div style={{ textAlign: "center", color: theme === "dark" ? "white" : "black" }}>
                            *****************************************************************
                        </div>
                        <p style={{ margin: "auto", paddingBottom: "10px", fontSize: "20px", color: theme === "dark" ? "white" : "black" }}>Admin's Reply: {group.reply}</p>
                        {group.messages.map(message => (
                            <div key={message.id} className="animated-container">
                                <div className="message-content">
                                    <div>
                                        <strong>Email:</strong> {message.email}
                                    </div>
                                    <strong>Message:</strong> {message.message}
                                    {message.replied && <span> ✓</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        );
    };

    const groupMessagesByAdminReply = (messages) => {
        const groupedByReply = {};
        messages.forEach(message => {
            const reply = message.replyMessage;
            const userEmail = message.email;
            const key = `${reply}_${userEmail}`;
            if (!groupedByReply[key]) {
                groupedByReply[key] = [];
            }
            groupedByReply[key].push(message);
        });

        return Object.entries(groupedByReply).map(([key, messages]) => {
            const [reply, userEmail] = key.split('_');
            return {
                reply,
                userEmail,
                messages
            };
        });
    };

    return (
        <div className={`page-container ${theme}`}>
            <div className="container">
                <div className="row">
                    <div className="col-md-12">

                        {renderUnrepliedMessages()}

                        {renderRepliedMessages()}

                        {/* Reply Modal */}
                        <Modal show={showReplyModal} onHide={handleCloseReplyModal} centered>
                            <Modal.Header closeButton>
                                <Modal.Title>Reply to {selectedEmail}</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                            <textarea
                                placeholder="Type your reply here"
                                rows="5"
                                className="form-control"
                                value={replyMessage}
                                onChange={(e) => setReplyMessage(e.target.value)}
                            ></textarea>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={handleCloseReplyModal}>
                                    Cancel
                                </Button>
                                <Button variant="primary" onClick={handleSendReply} disabled={loading || !replyMessage.trim()}>
                                    {loading ? "Sending..." : "Send Reply"}
                                </Button>
                            </Modal.Footer>
                        </Modal>

                        {/* Success Message */}
                        <Modal show={!!successMessage} onHide={() => setSuccessMessage("")} centered>
                            <Modal.Header closeButton>
                                <Modal.Title>Success</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>{successMessage}</Modal.Body>
                        </Modal>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Viewmessages;
